import { Route, Switch, Redirect } from 'wouter';
import DashboardPage from '../pages/dashboard';
import DailyMarketPage from '../pages/daily-market';
import EvolutionMarketPage from '../pages/evolution-market';
import RetailMarketPage from '../pages/retail-market';
import LiquidationsSummary from '../pages/settlements/settlements-summary';
import LiquidationsDetails from '../pages/settlements/settlements-details';
import SettlementInbox from '../pages/settlements/inbox';

import SettlementId from '../pages/settlements/settlements-details/settlements[id]';

import LiquidationsComparativeClients from '../pages/settlements/settlements-comparative-clients';

import useUserStore from '../../../common/src/stores/useUserStore';
import SuperadminExporters from '../pages/superadmin/exporters';
import SuperadminInboxAddresses from '../pages/superadmin/inbox-addresses';
import SuperadminPeople from '../pages/superadmin/people';
import SuperadminDocuments from '../pages/superadmin/documents';
import SuperadminVolume from '../pages/superadmin/volume';
import SuperAdminSettings from '../pages/superadmin/settings';
import useDataStore from '../../../common/src/stores/useDataStore';
import { useEffect, useRef, memo } from 'react';
import SuperadminEmails from '../pages/superadmin/emails';
import SuperadminSettlements from '../pages/superadmin/settlements';
import SuperadminExcelRules from '../pages/superadmin/excel-rules';
import SuperadminAnalytics from '../pages/superadmin/analytics';
import SuperadminUpdates from '../pages/superadmin/updates';
import SettlementsComparativeExporters from '../pages/settlements/settlements-comparative-exporters';
import SettlementsComparativeExportersEvolution from '../pages/settlements/settlements-comparative-exporters-evolution';
import SuperadminShips from '../pages/superadmin/ships';
import SuperadminShipments from '../pages/superadmin/shipments';
import SettlementsControlPage from '../pages/settlements/settlements-control';
import SuperadminSpecies from '../pages/superadmin/species';
import SuperadminRetailSkus from '../pages/superadmin/retail-skus';
import SuperadminMarkets from '../pages/superadmin/markets';
import SuperadminCurrencies from '../pages/superadmin/currencies';
import { getExporterSpecies } from 'common/src/api/species';
import ClaimsPage from '../pages/settlements/claims';
import ShipmentCasesPage from '../pages/shipment-cases';
import ClaimDetailPage from '../pages/settlements/claims/[id]';
import SiniestrosPage from '../pages/settlements/siniestros';
import SiniestroDetailPage from '../pages/settlements/siniestros/[id]';
import BusinessPage from '../pages/business';
import AccessDeniedPage from '../pages/access-denied';
import SettlementsComparativeSeasons from '../pages/settlements/settlements-comparative-seasons';
import ShipmentsDetails from '../pages/shipments/shipments-details';
import ShipmentsSummary from '../pages/shipments/shipments-summary';
import FrescuraPage from '../pages/shipments/frescura';
import ArrivalManagerPage from '../pages/shipments/arrival-manager';
import SchedulePage from '../pages/shipments/schedule';
import SettingsPage from '../pages/settings';
import SettlementsComparativeSales from '../pages/settlements/settlements-comparative-sales';
import SettlementsComparativeCosts from '../pages/settlements/settlements-comparative-costs';
import {
  CommercialPlanDetailPage,
  CommercialPlanSummaryPage,
} from '../pages/commercial-plan';
import SettlementsComparativeExportersUnified from '../pages/settlements/settlements-comparative-exporters-unified';
import SettlementsComparativeIndustry from '../pages/settlements/settlements-comparative-industry';
import SettlementsLiquidatedMix from '../pages/settlements/settlements-liquidated-mix';
import CuentaCorrientePage from '../pages/current-account';
import SuperadminReports from '../pages/superadmin/reports';
import SuperadminClients from '../pages/superadmin/clients';
import SuperadminImporters from '../pages/superadmin/importers';
import SuperadminSharingPermissions from '../pages/superadmin/sharing-permissions';
import ReportEditorPage from '../pages/superadmin/reports/editor';
import ReportAnalyticsPage from '../pages/superadmin/reports/analytics';
import ReportViewPage from '../pages/reports/[slug]';
import SuperadminContainersTracking from '../pages/superadmin/containers-tracking';
import ContainersSummaryPage from '../pages/superadmin/containers-summary';
import ShipsGoTrackingOperationsPage from '../pages/superadmin/shipsgo-tracking';
import LogbookPage from '../pages/logbook';
import PreSettlementsPageNew from '../pages/pre-settlements-new';
import SuperadminGenerateIntegration from '../pages/superadmin/generate-integration';
import SuperadminAIExamples from '../pages/superadmin/ai-examples';
import SuperadminChat from '../pages/superadmin/chat';
import SuperadminReceivers from '../pages/superadmin/receivers';
import USDAMarketExplorer from '../pages/usda-market-explorer';
import ClientReportsPage from '../pages/client-reports';
import IndustryVolumePage from '../pages/industry-volume';
import NewClientReportPage from '../pages/client-reports/new';
import SuperadminApiAccess from '../pages/superadmin/api-access';
import { FINDTEU_TRACKING_ENABLED } from 'common/src/utils/tracking-providers';

// Mapa de rutas a permisos necesarios
const routePermissionMap: Record<string, string> = {
  '/claims': 'claims',
  '/claims/summary': 'claims',
  '/claims/details': 'claims',
  '/claims/my-actions': 'claims',
  '/dashboard': 'dashboard',
  '/daily-market': 'daily-market',
  '/evolution-market': 'evolution-market',
  '/logbook': 'logbook',
  '/settlements/summary': 'settlements-sumarry',
  '/settlements/details': 'Detalle',
  '/settlements/inbox': 'Detalle',
  '/settlements/control': 'control',
  '/settlements/comparative-clients': 'comparative-clients',
  '/settlements/comparative-exporters': 'comparative-exporters',
  '/settlements/comparative-exporters-evolution':
    'comparative-exporters-evolution',
  '/settlements/comparative-exporters-costs': 'comparative-exporters-costs',
  '/settlements/comparative-industry': 'comparative-industry',
  '/settlements/liquidated-mix': 'production-industry',
  '/industry-volume': 'industry-volume-group',
  '/settlements/shipments-industry': 'production-industry',
  '/settlements/siniestros': 'siniestros',
  '/settlements/comparative-sales': 'comparative-sales',
  '/pre-settlements': 'pre-settlements',
  '/business': 'business-summary',
  '/team': 'team',
  '/commercial-plan': 'commercial-plan',
  '/commercial-plan/summary': 'commercial-plan',
  '/commercial-plan/detail': 'commercial-plan',
  '/shipments/summary': 'shipments-details',
  '/shipments/details': 'shipments-details',
  '/shipments/frescura': 'shipments-frescura',
  '/shipments/arrivals': 'shipments-details',
  '/shipments/schedule': 'shipments-details',
  '/current-account': 'current-account',
  '/client-reports': 'client-reports',
  '/client-reports/new': 'client-reports',
};

// Función para verificar el permiso basado en la ruta
const hasPermissionForRoute = (
  permissions: Array<{ sidebarItemKey: string; enabled: boolean }> | undefined,
  route: string
): boolean => {
  if (!permissions) return false;

  const requiredPermission = routePermissionMap[route];
  if (!requiredPermission) {
    return false;
  }

  return permissions.some(
    (p) => p.sidebarItemKey === requiredPermission && p.enabled
  );
};

// Separamos la lógica de especies en un componente que se monta una sola vez
const SpeciesInitializer = memo(() => {
  const { userProfile } = useUserStore();
  const { selectedSpecies, setSelectedSpecies } = useDataStore();
  const hasTriedSelectingSpeciesRef = useRef(false);
  const hasRefreshedSpeciesRef = useRef(false);

  useEffect(() => {
    // Este useEffect se ejecuta demasiadas veces - añadimos una condición para salir rápido
    if (
      !userProfile ||
      !userProfile.exporter_id ||
      hasTriedSelectingSpeciesRef.current
    ) {
      return;
    }

    const fetchAndSelectDefaultSpecies = async () => {
      // Only proceed for admin users that aren't superadmins
      if (
        (userProfile.roles.includes('admin') ||
          userProfile.roles.includes('basic')) &&
        !userProfile.roles.includes('superadmin')
      ) {
        hasTriedSelectingSpeciesRef.current = true;

        try {
          const exporter_id = userProfile.exporter_id as number;
          const fetchedSpecies = await getExporterSpecies(exporter_id);

          if (fetchedSpecies.length > 0) {
            // If we already have a selected species, refresh it with fresh data
            if (selectedSpecies) {
              const freshSelectedSpecies = fetchedSpecies.find(
                (s) => s.id === selectedSpecies.id
              );
              if (freshSelectedSpecies) {
                setSelectedSpecies(freshSelectedSpecies);
              }
            } else {
              // No species selected yet, select the first one
              const firstSpecies = fetchedSpecies[0];
              setSelectedSpecies(firstSpecies);
            }
          }
        } catch (error) {
          console.error('Error fetching default species:', error);
          hasTriedSelectingSpeciesRef.current = false;
        }
      }
    };

    fetchAndSelectDefaultSpecies();
  }, [userProfile, selectedSpecies, setSelectedSpecies]);

  return null; // Este componente no renderiza nada, solo ejecuta el efecto
});

// Inicializar datos de mercado
const MarketDataLoader = memo(() => {
  const { userProfile } = useUserStore();
  const { fetchAllMarketData, allMarketData } = useDataStore();

  useEffect(() => {
    if (
      (userProfile?.roles.includes('admin') ||
        userProfile?.roles.includes('basic')) &&
      !allMarketData
    ) {
      fetchAllMarketData();
    }
  }, [userProfile?.roles, allMarketData, fetchAllMarketData]);

  return null; // No renderiza nada
});

// Componente memoizado que verifica si el usuario tiene permiso para acceder
const ProtectedRoute = memo(
  ({
    component: Component,
    path,
    requiredPermission,
  }: {
    component: React.ComponentType;
    path: string;
    requiredPermission: string;
  }) => {
    const { userProfile } = useUserStore();

    // Si el usuario no está autenticado, redirigir a la página principal
    if (!userProfile) {
      return <Redirect to='/' />;
    }

    // Si el usuario es admin o superadmin, permitir acceso
    if (
      userProfile.roles.includes('admin') ||
      userProfile.roles.includes('superadmin')
    ) {
      return <Component />;
    }

    // Si el usuario es basic, verificar permisos
    if (userProfile.roles.includes('basic')) {
      const routePermissionCheck = hasPermissionForRoute(
        userProfile.permissions,
        path
      );

      const specificPermissionCheck = userProfile.permissions?.some(
        (p) => p.sidebarItemKey === requiredPermission && p.enabled
      );

      // Si alguna de las verificaciones es positiva, permitir acceso
      if (routePermissionCheck || specificPermissionCheck) {
        return <Component />;
      }
    }

    // Si el usuario no tiene permiso, mostrar página de acceso denegado
    return <AccessDeniedPage />;
  }
);

// Memoizamos los grupos de rutas para evitar recreaciones
const SuperAdminRoutes = memo(() => (
  <Switch>
    <Route path='/claims/:view?' component={ShipmentCasesPage} />
    <Route path='/' component={() => <Redirect to='/superadmin-analytics' />} />
    <Route path='/superadmin-exporters' component={SuperadminExporters} />
    <Route
      path='/superadmin-inbox-addresses'
      component={SuperadminInboxAddresses}
    />
    <Route path='/settlements/inbox' component={SettlementInbox} />
    <Route path='/superadmin-importers' component={SuperadminImporters} />
    <Route path='/superadmin-people' component={SuperadminPeople} />
    <Route path='/superadmin-markets' component={SuperadminMarkets} />
    <Route path='/superadmin-currencies' component={SuperadminCurrencies} />
    <Route path='/superadmin-shipments' component={SuperadminShipments} />
    <Route path='/shipments/frescura' component={FrescuraPage} />
    <Route path='/shipments/arrivals' component={ArrivalManagerPage} />
    {FINDTEU_TRACKING_ENABLED && (
      <Route
        path='/superadmin-containers-tracking'
        component={SuperadminContainersTracking}
      />
    )}
    <Route
      path='/superadmin-containers-summary'
      component={ContainersSummaryPage}
    />
    <Route
      path='/superadmin-shipsgo-tracking'
      component={ShipsGoTrackingOperationsPage}
    />

    <Route path='/superadmin-documents' component={SuperadminDocuments} />
    <Route path='/superadmin-volume' component={SuperadminVolume} />
    <Route path='/superadmin-settings' component={SuperAdminSettings} />
    <Route
      path='/superadmin-sharing-permissions'
      component={SuperadminSharingPermissions}
    />
    <Route path='/superadmin-emails' component={SuperadminEmails} />
    <Route path='/superadmin-excel-rules' component={SuperadminExcelRules} />
    <Route path='/superadmin-settlements' component={SuperadminSettlements} />
    <Route path='/superadmin-analytics' component={SuperadminAnalytics} />
    <Route path='/superadmin-clients' component={SuperadminClients} />
    <Route path='/superadmin-api-access' component={SuperadminApiAccess} />
    <Route path='/superadmin-updates' component={SuperadminUpdates} />
    <Route path='/superadmin-ships' component={SuperadminShips} />
    <Route path='/superadmin-species' component={SuperadminSpecies} />
    <Route path='/superadmin-retail-skus' component={SuperadminRetailSkus} />
    <Route path='/superadmin-reports' component={SuperadminReports} />
    <Route path='/superadmin-reports/create' component={ReportEditorPage} />
    <Route path='/superadmin-reports/edit/:id' component={ReportEditorPage} />
    <Route
      path='/superadmin-reports/analytics/:id'
      component={ReportAnalyticsPage}
    />
    <Route
      path='/superadmin-generate-integration'
      component={SuperadminGenerateIntegration}
    />
    <Route path='/superadmin-ai-examples' component={SuperadminAIExamples} />
    <Route path='/superadmin-chat' component={SuperadminChat} />
    <Route path='/superadmin-receivers' component={SuperadminReceivers} />
    <Route path='/usda-market-explorer' component={USDAMarketExplorer} />
    <Route path='/reports/:slug' component={ReportViewPage} />
  </Switch>
));

const DocumentHelperRoutes = memo(() => (
  <Switch>
    <Route path='/' component={() => <Redirect to='/superadmin-documents' />} />
    <Route path='/superadmin-documents' component={SuperadminDocuments} />
    <Route path='/superadmin-retail-skus' component={SuperadminRetailSkus} />
    <Route path='/access-denied' component={AccessDeniedPage} />
  </Switch>
));

const AdminRoutes = memo(() => (
  <Switch>
    <Route path='/claims/:view?' component={ShipmentCasesPage} />
    <Route path='/' component={() => <Redirect to='/dashboard' />} />
    <Route path='/dashboard' component={DashboardPage} />
    <Route path='/business' component={BusinessPage} />
    <Route path='/settings' component={SettingsPage} />
    {/* Equipo ahora es una pestaña de Configuración. */}
    <Route path='/team' component={() => <Redirect to='/settings#team' />} />
    <Route path='/shipments/summary' component={ShipmentsSummary} />
    <Route path='/shipments/details' component={ShipmentsDetails} />
    <Route path='/shipments/frescura' component={FrescuraPage} />
    <Route path='/shipments/arrivals' component={ArrivalManagerPage} />
    <Route path='/shipments/schedule' component={SchedulePage} />
    <Route
      path='/commercial-plan'
      component={() => <Redirect to='/commercial-plan/summary' />}
    />
    <Route
      path='/commercial-plan/summary'
      component={CommercialPlanSummaryPage}
    />
    <Route
      path='/commercial-plan/detail'
      component={CommercialPlanDetailPage}
    />
    <Route path='/daily-market' component={DailyMarketPage} />
    <Route path='/evolution-market' component={EvolutionMarketPage} />
    <Route path='/retail-market' component={RetailMarketPage} />
    <Route path='/usda-market-explorer' component={USDAMarketExplorer} />
    <Route path='/logbook' component={LogbookPage} />
    <Route path='/settlements/summary' component={LiquidationsSummary} />
    <Route path='/settlements/details' component={LiquidationsDetails} />
    <Route path='/settlements/inbox' component={SettlementInbox} />
    <Route path='/settlements/details/:id' component={SettlementId} />
    <Route path='/settlements/claims' component={ClaimsPage} />
    <Route path='/settlements/claims/:id' component={ClaimDetailPage} />
    <Route path='/settlements/siniestros' component={SiniestrosPage} />
    <Route path='/settlements/siniestros/:id' component={SiniestroDetailPage} />
    <Route
      path='/settlements/comparative-seasons'
      component={SettlementsComparativeSeasons}
    />
    <Route
      path='/settlements/comparative-exporters-sales'
      component={SettlementsComparativeSales}
    />

    <Route
      path='/settlements/comparative-clients'
      component={LiquidationsComparativeClients}
    />
    <Route
      path='/settlements/comparative-industry'
      component={SettlementsComparativeIndustry}
    />
    <Route
      path='/settlements/production'
      component={() => <Redirect to='/settlements/liquidated-mix' />}
    />
    <Route
      path='/settlements/shipments-industry'
      component={() => <Redirect to='/settlements/liquidated-mix' />}
    />
    <Route
      path='/settlements/liquidated-mix'
      component={SettlementsLiquidatedMix}
    />
    <Route
      path='/settlements/comparative-exporters-evolution'
      component={SettlementsComparativeExportersEvolution}
    />
    <Route
      path='/settlements/comparative-exporters-unified'
      component={SettlementsComparativeExportersUnified}
    />
    <Route
      path='/settlements/comparative-exporters'
      component={SettlementsComparativeExporters}
    />
    <Route
      path='/settlements/comparative-exporters-costs'
      component={SettlementsComparativeCosts}
    />
    <Route path='/settlements/control' component={SettlementsControlPage} />
    <Route path='/pre-settlements' component={PreSettlementsPageNew} />

    <Route path='/current-account' component={CuentaCorrientePage} />
    <Route path='/client-reports' component={ClientReportsPage} />
    <Route path='/client-reports/new' component={NewClientReportPage} />
    {/* Módulo Volumen: todavía sin entrada en el menú. */}
    <Route
      path='/industry-volume'
      component={() => (
        <ProtectedRoute
          component={IndustryVolumePage}
          path='/industry-volume'
          requiredPermission='industry-volume-group'
        />
      )}
    />
    <Route path='/usda-market-explorer' component={USDAMarketExplorer} />
    <Route path='/reports/:slug' component={ReportViewPage} />
    <Route path='/access-denied' component={AccessDeniedPage} />
  </Switch>
));

const BasicUserRoutes = memo(() => (
  <Switch>
    <Route path='/' component={() => <Redirect to='/daily-market' />} />
    <Route
      path='/dashboard'
      component={() => (
        <ProtectedRoute
          component={DashboardPage}
          path='/dashboard'
          requiredPermission='dashboard'
        />
      )}
    />
    <Route
      path='/business'
      component={() => (
        <ProtectedRoute
          component={BusinessPage}
          path='/business'
          requiredPermission='business-summary'
        />
      )}
    />
    <Route
      path='/settings'
      component={() => (
        <ProtectedRoute
          component={SettingsPage}
          path='/settings'
          requiredPermission='settings'
        />
      )}
    />

    <Route
      path='/daily-market'
      component={() => (
        <ProtectedRoute
          component={DailyMarketPage}
          path='/daily-market'
          requiredPermission='daily-market'
        />
      )}
    />
    <Route path='/usda-market-explorer' component={USDAMarketExplorer} />
    <Route path='/retail-market' component={RetailMarketPage} />
    <Route
      path='/evolution-market'
      component={() => (
        <ProtectedRoute
          component={EvolutionMarketPage}
          path='/evolution-market'
          requiredPermission='evolution-market'
        />
      )}
    />
    <Route
      path='/logbook'
      component={() => (
        <ProtectedRoute
          component={LogbookPage}
          path='/logbook'
          requiredPermission='logbook'
        />
      )}
    />
    <Route
      path='/shipments/arrivals'
      component={() => (
        <ProtectedRoute
          component={ArrivalManagerPage}
          path='/shipments/arrivals'
          requiredPermission='shipments-details'
        />
      )}
    />
    <Route
      path='/shipments/summary'
      component={() => (
        <ProtectedRoute
          component={ShipmentsSummary}
          path='/shipments/summary'
          requiredPermission='shipments-details'
        />
      )}
    />
    <Route
      path='/shipments/details'
      component={() => (
        <ProtectedRoute
          component={ShipmentsDetails}
          path='/shipments/details'
          requiredPermission='shipments-details'
        />
      )}
    />
    <Route
      path='/commercial-plan'
      component={() => <Redirect to='/commercial-plan/summary' />}
    />
    <Route
      path='/commercial-plan/summary'
      component={() => (
        <ProtectedRoute
          component={CommercialPlanSummaryPage}
          path='/commercial-plan/summary'
          requiredPermission='commercial-plan'
        />
      )}
    />
    <Route
      path='/commercial-plan/detail'
      component={() => (
        <ProtectedRoute
          component={CommercialPlanDetailPage}
          path='/commercial-plan/detail'
          requiredPermission='commercial-plan'
        />
      )}
    />
    <Route
      path='/settlements/summary'
      component={() => (
        <ProtectedRoute
          component={LiquidationsSummary}
          path='/settlements/summary'
          requiredPermission='settlements-sumarry'
        />
      )}
    />
    <Route
      path='/settlements/details'
      component={() => (
        <ProtectedRoute
          component={LiquidationsDetails}
          path='/settlements/details'
          requiredPermission='Detalle'
        />
      )}
    />
    <Route
      path='/settlements/inbox'
      component={() => (
        <ProtectedRoute
          component={SettlementInbox}
          path='/settlements/inbox'
          requiredPermission='Detalle'
        />
      )}
    />
    <Route
      path='/settlements/details/:id'
      component={() => (
        <ProtectedRoute
          component={SettlementId}
          path='/settlements/details/:id'
          requiredPermission='Detalle'
        />
      )}
    />
    <Route
      path='/settlements/comparative-clients'
      component={() => (
        <ProtectedRoute
          component={LiquidationsComparativeClients}
          path='/settlements/comparative-clients'
          requiredPermission='comparative-clients'
        />
      )}
    />
    <Route
      path='/settlements/comparative-industry'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeIndustry}
          path='/settlements/comparative-industry'
          requiredPermission='comparative-industry'
        />
      )}
    />
    <Route
      path='/settlements/production'
      component={() => <Redirect to='/settlements/liquidated-mix' />}
    />
    <Route
      path='/settlements/shipments-industry'
      component={() => <Redirect to='/settlements/liquidated-mix' />}
    />
    <Route
      path='/settlements/liquidated-mix'
      component={() => (
        <ProtectedRoute
          component={SettlementsLiquidatedMix}
          path='/settlements/liquidated-mix'
          requiredPermission='production-industry'
        />
      )}
    />
    <Route
      path='/industry-volume'
      component={() => (
        <ProtectedRoute
          component={IndustryVolumePage}
          path='/industry-volume'
          requiredPermission='industry-volume-group'
        />
      )}
    />
    <Route
      path='/settlements/comparative-seasons'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeSeasons}
          path='/settlements/comparative-seasons'
          requiredPermission='comparative-seasons'
        />
      )}
    />
    <Route
      path='/settlements/comparative-exporters-sales'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeSales}
          path='/settlements/comparative-exporters-sales'
          requiredPermission='comparative-exporters-sales'
        />
      )}
    />
    <Route
      path='/settlements/comparative-exporters-evolution'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeExportersEvolution}
          path='/settlements/comparative-exporters-evolution'
          requiredPermission='comparative-exporters-evolution'
        />
      )}
    />
    <Route
      path='/settlements/comparative-exporters-unified'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeExportersUnified}
          path='/settlements/comparative-exporters-unified'
          requiredPermission='comparative-exporters-unified'
        />
      )}
    />
    <Route
      path='/settlements/comparative-exporters'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeExporters}
          path='/settlements/comparative-exporters'
          requiredPermission='comparative-exporters'
        />
      )}
    />
    <Route
      path='/settlements/comparative-exporters-costs'
      component={() => (
        <ProtectedRoute
          component={SettlementsComparativeCosts}
          path='/settlements/comparative-exporters-costs'
          requiredPermission='comparative-exporters-costs'
        />
      )}
    />
    <Route
      path='/settlements/control'
      component={() => (
        <ProtectedRoute
          component={SettlementsControlPage}
          path='/settlements/control'
          requiredPermission='control'
        />
      )}
    />
    <Route
      path='/pre-settlements'
      component={() => (
        <ProtectedRoute
          component={PreSettlementsPageNew}
          path='/pre-settlements'
          requiredPermission='pre-settlements'
        />
      )}
    />
    <Route
      path='/settlements/claims'
      component={() => (
        <ProtectedRoute
          component={ClaimsPage}
          path='/settlements/claims'
          requiredPermission='claims'
        />
      )}
    />
    <Route
      path='/claims/:view?'
      component={() => (
        <ProtectedRoute
          component={ShipmentCasesPage}
          path='/claims'
          requiredPermission='claims'
        />
      )}
    />
    <Route
      path='/settlements/claims/:id'
      component={() => (
        <ProtectedRoute
          component={ClaimDetailPage}
          path='/settlements/claims/:id'
          requiredPermission='claims'
        />
      )}
    />
    <Route
      path='/settlements/siniestros'
      component={() => (
        <ProtectedRoute
          component={SiniestrosPage}
          path='/settlements/siniestros'
          requiredPermission='siniestros'
        />
      )}
    />
    <Route
      path='/settlements/siniestros/:id'
      component={() => (
        <ProtectedRoute
          component={SiniestroDetailPage}
          path='/settlements/siniestros/:id'
          requiredPermission='siniestros'
        />
      )}
    />
    <Route
      path='/current-account'
      component={() => (
        <ProtectedRoute
          component={CuentaCorrientePage}
          path='/current-account'
          requiredPermission='current-account'
        />
      )}
    />
    <Route
      path='/client-reports'
      component={() => (
        <ProtectedRoute
          component={ClientReportsPage}
          path='/client-reports'
          requiredPermission='client-reports'
        />
      )}
    />
    <Route
      path='/client-reports/new'
      component={() => (
        <ProtectedRoute
          component={NewClientReportPage}
          path='/client-reports/new'
          requiredPermission='client-reports'
        />
      )}
    />
    <Route path='/reports/:slug' component={ReportViewPage} />
    <Route path='/access-denied' component={AccessDeniedPage} />
  </Switch>
));

// Componente principal de rutas
const RoutesConfig = () => {
  const { userProfile } = useUserStore();

  return (
    <>
      {/* Estos componentes se montan una sola vez y manejan la inicialización de datos */}
      <SpeciesInitializer />
      <MarketDataLoader />

      {/* Renderizamos las rutas según el rol */}
      {userProfile?.roles?.includes('superadmin') && <SuperAdminRoutes />}
      {userProfile?.roles?.includes('admin') && <AdminRoutes />}
      {userProfile?.roles?.includes('basic') && <BasicUserRoutes />}
      {userProfile?.roles?.includes('document_helper') && (
        <DocumentHelperRoutes />
      )}

      {/* Si no hay usuario o no tiene un rol reconocido */}
      {(!userProfile ||
        !(
          userProfile.roles?.includes('superadmin') ||
          userProfile.roles?.includes('admin') ||
          userProfile.roles?.includes('basic') ||
          userProfile.roles?.includes('document_helper')
        )) && <Redirect to='/' />}
    </>
  );
};

export default RoutesConfig;
